import { addBreadcrumb } from '@sentry/react';

const WATCHED_FIELD_NAMES = ['username', 'password', 'csrf_token'];
const MAX_STACK_LENGTH = 1500;

type WatchedField = {
    name: string;
    element: HTMLInputElement;
};

type FieldSnapshot = {
    connected: boolean;
    inForm: boolean;
    disabled: boolean;
    valueLength: number;
    matchingFieldsInForm: number;
};

export type FormClearingWatcher = {
    arm(): void;
    disarm(): void;
    recordSnapshot(): void;
};

function captureStack(): string {
    return (new Error().stack ?? '').slice(0, MAX_STACK_LENGTH);
}

function record(message: string, data: Record<string, unknown>): void {
    try {
        addBreadcrumb({ category: 'login.form_clearing', level: 'warning', message, data });
    } catch {
        // Telemetry must never break the submit flow.
    }
}

function interceptValueClearing(fields: WatchedField[], isArmed: () => boolean): void {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    const getValue = descriptor?.get;
    const setValue = descriptor?.set;

    if (!getValue || !setValue) {
        return;
    }

    fields.forEach(({ name, element }) => {
        Object.defineProperty(element, 'value', {
            configurable: true,
            get(this: HTMLInputElement): string {
                return getValue.call(this) as string;
            },
            set(this: HTMLInputElement, value: string): void {
                if (isArmed() && String(value) === '') {
                    record('field value cleared', { field: name, stack: captureStack() });
                }

                setValue.call(this, value);
            }
        });
    });
}

function observeFieldMutations(
    form: HTMLFormElement,
    fields: WatchedField[],
    isArmed: () => boolean
): void {
    const observer = new MutationObserver((mutations) => {
        if (!isArmed()) {
            return;
        }

        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
                record('field attribute changed', {
                    field: (mutation.target as Element).getAttribute('name'),
                    attribute: mutation.attributeName
                });
                return;
            }

            const removedFields = fields
                .filter(({ element }) =>
                    Array.from(mutation.removedNodes).some(
                        (node) => node === element || node.contains(element)
                    )
                )
                .map(({ name }) => name);

            if (removedFields.length > 0) {
                record('field removed from form', { fields: removedFields });
            }
        });
    });

    observer.observe(form, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'name', 'form', 'value']
    });
}

/**
 * Diagnostic-only: records how the login fields disappear from the form while the captcha
 * is solving (value cleared by a script, attribute change, node removal, or form reset).
 * Records only while armed, and never logs field values.
 */
export default function watchFormClearing(form: HTMLFormElement): FormClearingWatcher {
    let armed = false;
    const isArmed = (): boolean => armed;

    const fields = WATCHED_FIELD_NAMES.map((name) => ({
        name,
        element: form.querySelector<HTMLInputElement>(`input[name="${name}"]`)
    })).filter((field): field is WatchedField => field.element !== null);

    interceptValueClearing(fields, isArmed);
    observeFieldMutations(form, fields, isArmed);

    form.addEventListener('reset', () => {
        if (armed) {
            record('form reset', { stack: captureStack() });
        }
    });

    return {
        arm: (): void => {
            armed = true;
        },
        disarm: (): void => {
            armed = false;
        },
        recordSnapshot: (): void => {
            const snapshot: Record<string, FieldSnapshot> = Object.fromEntries(
                fields.map(({ name, element }) => [
                    name,
                    {
                        connected: element.isConnected,
                        inForm: element.form === form,
                        disabled: element.disabled,
                        valueLength: element.value.length,
                        matchingFieldsInForm: form.querySelectorAll(`input[name="${name}"]`).length
                    }
                ])
            );

            record('field snapshot at captcha success', snapshot);
        }
    };
}
